import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store'; // Import AppDispatch if it's defined in your store setup
import GamicDetails from './GamicDetails';
import GamicConsoleReport from './GamicConsoleReport';
import GamicPublisherDetail from './GamicPublisherDetail';
import { Icons } from '@/icons';
import { AdminGamic, Status, UserType } from './types';
import {
  deleteGamic,
  fetchAllGamicsAdmin,
  updateGamicStatus,
} from '@/store/Gamic';
import { fetchUserByAdmin } from '@/store/User';
import GamicsDialog from '@/components/dialog/GamicsDialog';
import ApproveRejectDialog from '@/components/dialog/ApprovedDialog';

const PreviewGamic: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [activeTab, setActiveTab] = useState<
    'details' | 'console' | 'publisher'
  >('details');

  const { state } = useLocation();
  const navigate = useNavigate();
  const gamic = (state as { gamic?: AdminGamic })?.gamic;
  const [user, setUser] = useState<UserType>();
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  // const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [reasons, setReasons] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<
    'removed' | 'approved' | 'rejected' | 'hold' | 'publish' | null
  >(null);

  const gamicFromRedux = useSelector((s: RootState) =>
    s.gamics.adminList.find((g) => g.id === state?.gamic.id),
  );

  // console.log("gamicFromRedux" ,gamicFromRedux);
  

  /* Kick back if no data (hard-refresh / direct URL) */
useEffect(() => {
  if (!gamic || !gamicFromRedux) {
    navigate('/admin/gamics');
  }
}, [gamic, gamicFromRedux, navigate]);


  useEffect(() => {
    const data = async () => {
      dispatch(fetchAllGamicsAdmin({}));
      if (gamic) {
        const resU = await dispatch(fetchUserByAdmin(gamic.byUser));
        setUser(resU.payload.data[0]);
      }
    };
    data();
  }, []);

  if (!gamic || !user || !gamicFromRedux) return null;

  const close = () => {
    setIsApproveOpen(false);
    setIsRejectOpen(false);
    // setIsRemoveOpen(false);
    setReasons([]);
  };

  const handleApprove = async () => {
    try {
      await dispatch(
        updateGamicStatus({
          gamicId: gamic.id,
          status: 'approved',
          tags: reasons,
        }),
      );
      setDialogType('approved');
      setDialogOpen(true);
      dispatch(fetchAllGamicsAdmin({}));
      close();
    } catch (error) {
      console.error('Error updating gamic status:', error);
      close();
    }
  };

  const handleReject = async () => {
    try {
      await dispatch(
        updateGamicStatus({
          gamicId: gamic.id,
          status: 'rejected',
          tags: reasons,
        }),
      );
      setDialogType('rejected');
      setDialogOpen(true);
      dispatch(fetchAllGamicsAdmin({}));
      close();
    } catch (error) {
      console.error('Error updating gamic status:', error);
      close();
    }
  };

  const handleRemove = async () => {
    try {
      await dispatch(deleteGamic(gamic.id));
      setDialogType('removed');
      setDialogOpen(true);

      dispatch(fetchAllGamicsAdmin({}));
      close();
    } catch (error) {
      console.error('Error updating gamic status:', error);
      close();
    }
  };

  const formatSingle = (g: any): AdminGamic => ({
    id: g.id,
    file: g.file_url,
    img: g.image_url,
    timestamp: new Date(g.created_at)
      .toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
      .replace(/[^\w]/g, '/'),
    title: g.title,
    category: g.categories?.[0] || 'Uncategorized',
    tags: g.tags,
    description: g.description || 'No description provided',
    byUser: g.user_id,
    downloadLink: g.download_url,
    // WL-DL-V1
    iosDownloadUrl: g.ios_download_url || '',
    androidDownloadUrl: g.android_download_url || '',
    isDownloadLink:
      g.ios_download_url || g.android_download_url || g.download_url
        ? 'Attached'
        : 'Not Attached',
    consoleResult: 'Pass',
    status: (g.status?.charAt(0).toUpperCase() + g.status?.slice(1)) as Status,
  });

  const gamicNew = formatSingle(gamicFromRedux)

  return (
    <div className='bg-white rounded-[24px] h-full flex flex-col'>
      {/* ───────── Tabs ───────── */}
      <div className='px-[20px] shadow-sm rounded-tl-[24px] rounded-tr-[24px]  bg-[#FFFFFF] pb-0 space-x-[32px] h-[77px] '>
        {(['details', 'console', 'publisher'] as const).map((k) => (
          <button
            key={k}
            onClick={() => setActiveTab(k)}
            className={`flex-1 text-center h-full text-[18px] ${
              activeTab === k
                ? 'border-b-2 border-[#FF0050] text-[#FF0050]'
                : 'text-gray-500'
            }`}
          >
            {k === 'details'
              ? 'Gamic Details'
              : k === 'console'
                ? 'Console Report'
                : 'Publisher Details'}
          </button>
        ))}
      </div>

      {/* ───────── Tab Content ───────── */}
      <div className='p-6 flex-1 overflow-y-auto scrollbar scrollbar-thumb-[#EEEEEE] scrollbar-track-transparent'>
        {activeTab === 'details' && <GamicDetails gamic={gamicNew} />}
        {activeTab === 'console' && <GamicConsoleReport gamic={gamicNew} />}
        {activeTab === 'publisher' && (
          <GamicPublisherDetail gamic={gamicNew} user={user} />
        )}
      </div>

      {/* ─── Actions ─── */}
      <div className='flex items-center justify-between pt-6 px-6 pb-6'>
        <button
          onClick={handleRemove}
          className='flex items-center px-4 py-2 border border-black rounded-[100px]
                     text-gray-700 hover:bg-gray-50'
        >
          <Icons.Delete className='mr-2 w-4 h-4' />
          Remove Gamic
        </button>

        {gamicNew.status.toLowerCase() === 'pending' && (
          <div className='flex gap-3'>
            <button
              onClick={() => setIsRejectOpen(true)}
              className='px-6 py-2 bg-[#FF0000]   text-white rounded-[100px] hover:bg-red-600'
            >
              Rejected
            </button>
            <button
              className='px-6 py-2 bg-[#1FAA03] text-white rounded-[100px] hover:bg-green-600'
              onClick={() => setIsApproveOpen(true)} // Trigger the approval action
            >
              Approve
            </button>
          </div>
        )}
      </div>
      <ApproveRejectDialog
        open={isApproveOpen}
        title='Approve Gamic'
        message='You are going to approve gamic'
        confirmLabel='Yes, Approve'
        cancelLabel='No, Cancel'
        onConfirm={handleApprove}
        onClose={close}
        setReasons={setReasons}
        reasons={reasons}
      />
      <ApproveRejectDialog
        open={isRejectOpen}
        title='Reject Gamic'
        message='You are going to reject gamic'
        confirmLabel='Yes, Reject'
        cancelLabel='No, Cancel'
        onConfirm={handleReject}
        onClose={close}
        setReasons={setReasons}
        reasons={reasons}
      />
      <GamicsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        type={dialogType!}
      />
    </div>
  );
};

export default PreviewGamic;
